import { compact } from 'lodash';
import { computed } from 'vue';

import { GlobalState } from '@/global';
import { adminHasFault, adminNeedsAttention, useAdminAttention } from '@/modules/admin/helpers/attention';
import { talkEmergencyActiveCount } from '@/modules/admin/helpers/talk-emergency';
import { TALK_OVERVIEW_PATH } from '@/modules/admin/helpers/talk-overview-nav';
import { canAccessFullSalespoint } from '@/modules/sales/subapps/salespoint/permissions';
import { canAccessSysAdmin, hasRouteAccess } from '@/shared/helpers/permissions.helpers';
import { Auth } from '@/shared/services/auth.service';

export interface ILauncherItem {
    name: string;
    path: string;
    icon: string;
    subItems?: {
        name: string;
        path: string;
        icon: string;
        mobileReady?: boolean;
        hidden?: boolean;
        // Optional extra visibility gate, evaluated (reactively) on top of route access. Use when an
        // item is route-reachable for a wider audience than should see the standalone launcher entry.
        gate?: () => boolean;
    }[];
    mobileReady?: boolean;
    hidden?: boolean;
    // Optional extra visibility gate, evaluated (reactively) on top of route access. Use when an
    // item is route-reachable for a wider audience than should see the launcher entry.
    gate?: () => boolean;
    // "Something in here needs you" — renders an attention dot on the sidebar entry. Reactive, so
    // it clears itself when whatever it points at is dealt with. For a count, use the sidebar's own
    // `count` (Talk unread); this is for state with no number to show.
    badge?: () => boolean;
    /** Hover text for that dot. Say what needs doing — the dot on its own carries no meaning. */
    badgeTitle?: string;
    // "Something in here is broken", as opposed to `badge`'s "something needs you" — renders a red
    // exclamation in place of the dot, and outranks it when both are true. Same contract otherwise.
    alert?: () => boolean;
    // Hover text for that marker. Say what is wrong, not just that something is. Resolved per render
    // when it's a function, so an item whose marker can stand for more than one fault names which.
    alertTitle?: string | (() => string);
    // Where the marker itself goes, when the fix lives somewhere deeper than the app's landing page.
    // Only the marker follows it — the rest of the entry still navigates to `path`.
    alertTo?: string;
}

export const LauncherItems: ILauncherItem[] = [
    {
        name: 'Customers & Contacts',
        path: '/crm',
        icon: 'fad fa-users',
        subItems: [{ name: 'Memberships', path: '/crm/memberships', icon: 'fad fa-id-card', mobileReady: true }],
        mobileReady: true
    },
    {
        name: 'Display',
        path: '/display',
        icon: 'fa fa-tv',
        subItems: [
            { name: 'Devices', path: '/display/devices', icon: 'fad fa-tv', mobileReady: true },
            { name: 'Display Groups', path: '/display/groups', icon: 'fad fa-layer-group', mobileReady: true },
            { name: 'Playlists', path: '/display/playlists', icon: 'fad fa-list-music', mobileReady: true },
            { name: 'Media Library', path: '/display/media', icon: 'fad fa-photo-film', mobileReady: true }
        ],
        mobileReady: true
    },
    {
        name: 'Forms',
        path: '/forms',
        icon: 'fad fa-file-lines',
        subItems: [
            { name: 'Submissions', path: '/forms/submissions', icon: 'fad fa-inbox', mobileReady: true },
            // The builder is desktop-only in practice — three panes don't fit a phone.
            { name: 'Form Builder', path: '/forms/manage', icon: 'fad fa-pen-ruler' }
        ],
        mobileReady: true
    },
    // Single launcher tile (no subItems): the Hosting module supplies its own `.zs-subnav`
    // (Sites / Analytics / Domains / Monitoring, with icons + the site picker and mobile drawer),
    // mirroring Admin and SysAdmin. Expanding the sidebar here too would double the section nav.
    // Friendly tile name differs from the "ZynoHosting" wordmark/breadcrumb (rootAppName), same as
    // CRM ("Customers & Contacts" → ZynoCRM) and Sales ("Sales & Bookings" → ZynoSales).
    { name: 'Domains & Hosting', path: '/hosting', icon: 'fad fa-cloud-arrow-up' },
    { name: 'Inventory', path: '/inventory', icon: 'fad fa-shelves', mobileReady: true },
    {
        name: 'Sales',
        path: '/sales',
        icon: 'fad fa-dollar-sign',
        subItems: [
            // Booking editors can reach salespoint (bookings-only mode) contextually from a booking,
            // but the standalone launcher entry is only for users who can run a full register.
            {
                name: 'Salespoint',
                path: '/sales/salespoint',
                icon: 'fad fa-bag-shopping',
                mobileReady: true,
                gate: () => canAccessFullSalespoint.value
            },
            { name: 'Bookings', path: '/sales/bookings', icon: 'fad fa-calendar-check', mobileReady: true },
            { name: 'Gift Cards', path: '/sales/gift-cards', icon: 'fad fa-gift-card', mobileReady: true },
            { name: 'Products', path: '/sales/config/products', icon: 'fad fa-boxes-stacked', mobileReady: true },
            { name: 'Reports', path: '/sales/reports', icon: 'fad fa-file-chart-column', mobileReady: true },
            { name: 'Transactions', path: '/sales/transactions', icon: 'fad fa-receipt', mobileReady: true },
            { name: 'Fulfillment', path: '/sales/fulfillment', icon: 'fad fa-box', mobileReady: true },
            { name: 'Cash Sessions', path: '/sales/cash-sessions', icon: 'fad fa-money-bill-transfer', mobileReady: true },
            { name: 'Daily Closeouts', path: '/sales/closeouts', icon: 'fad fa-calendar-check', mobileReady: true }
        ],
        mobileReady: true
    },
    // Talk is intentionally NOT mobile-ready yet.
    { name: 'Talk', path: '/talk', icon: 'fad fa-phone-alt' },
    // The MSP-partner console: visible only with the `msp.login` capability (route meta `msp: true`).
    // Quote building is a desktop workflow, so it is not mobile-ready.
    {
        name: 'MSP',
        path: '/msp',
        icon: 'fad fa-handshake',
        subItems: [
            // Gated to msp.inventoryRead by its route meta, so a partner without it sees only Quotes.
            { name: 'Tenants', path: '/msp/tenants', icon: 'fad fa-buildings' },
            { name: 'Talk Quotes', path: '/msp/talk-quotes', icon: 'fad fa-file-invoice-dollar' }
        ]
    },
    // RMM is scoped to the tenant you actually signed into. While masquerading into another
    // tenant (MSP bridge or sysadmin impersonation) the entry is hidden — the RMM console
    // belongs to the impersonator's own tenant, not the one being viewed.
    { name: 'RMM', path: '/rmm', icon: 'fad fa-desktop', mobileReady: true, gate: () => !Auth.impersonatorTenantId.value },
    // Admin is appended last; visibility is gated by the `isTenantAdmin` check
    // inside useAuthorizedLauncherItems below (route `meta.public: false` plus
    // a tenant.isAdmin guard) so it never appears for non-admin users.
    //
    // Badged while anything under Admin is waiting on this user — today ZynoTalk onboarding, whose
    // work lives at Admin → ZynoTalk → Overview and carries the same dot. The hover text stays
    // deliberately unspecific: the aggregate covers more than one source, so naming one would go
    // stale the moment a second lands. The dot draws the click; the screen behind it explains.
    {
        name: 'Admin',
        path: '/admin',
        icon: 'fad fa-gear',
        mobileReady: true,
        badge: () => adminNeedsAttention.value,
        badgeTitle: 'Something in here needs your attention',
        // Named, unlike the dot: the red marker means emergency calling and nothing else, so the
        // handful of things it can point at can each be named — and both are resolved on one screen,
        // which is where the marker goes rather than the Admin landing page.
        alert: () => adminHasFault.value,
        alertTitle: () =>
            talkEmergencyActiveCount.value > 0
                ? `A 911 call has left ${talkEmergencyActiveCount.value === 1 ? 'a number' : `${talkEmergencyActiveCount.value} numbers`} in emergency mode`
                : "An e911 location can't place 911 calls",
        alertTo: TALK_OVERVIEW_PATH
    },
    // Sysadmin is the cross-tenant operator console; visibility is gated by
    // canAccessSysAdmin below (platform admin + active tenant is ZC).
    { name: 'SysAdmin', path: '/sysadmin', icon: 'fad fa-shield-halved' }
];

export function useAuthorizedLauncherItems() {
    // Badge state is loaded here rather than by the item that shows it: this feeds both the sidebar
    // and the welcome tiles, so it is the one place that knows the badge predicates are about to be
    // evaluated. The calls are permission-gated and deduped per tenant, so a user who could never see
    // the badge never asks.
    useAdminAttention();

    return computed(() => {
        const requireMobileReady = !!GlobalState.enableMobileAppDesign.value;
        const isTenantAdmin = !!GlobalState.tenant.value?.isAdmin;
        return compact(
            LauncherItems.map(app => {
                // Admin is gated by tenant-admin status — non-admins never see it.
                if (app.path === '/admin' && !isTenantAdmin) return null;
                // Sysadmin is a platform-admin console, only surfaced inside the ZC master tenant.
                if (app.path === '/sysadmin' && !canAccessSysAdmin.value) return null;
                if (!(app.gate?.() ?? true)) return null;

                const hasAccess = hasRouteAccess(app.path);
                const subItems = app.subItems
                    ?.filter(subItem => hasRouteAccess(subItem.path) && (subItem.gate?.() ?? true))
                    .map(subItem => ({ ...subItem, hidden: requireMobileReady && !subItem.mobileReady }));

                if (!hasAccess && !subItems?.length) {
                    return null;
                }

                // Brand Sales as "Sales & Bookings" when the bookings module is enabled
                // (i.e. the user can reach the bookings screens).
                const name = app.path === '/sales' && hasRouteAccess('/sales/bookings') ? 'Sales & Bookings' : app.name;

                return { ...app, name, subItems, path: hasAccess ? app.path : '#', hidden: requireMobileReady && !app.mobileReady };
            })
        );
    });
}
